/**
 * AlertConfigurationPage — `/reporting/alerts`.
 *
 * Wired to real backend (B-slice 5):
 *   GET  /v1/reporting/alerts — list alert rules
 *   POST /v1/reporting/alerts — create an alert rule
 */
import { type JSX, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Bell, Plus } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import {
  listKpiAlerts,
  listKpiDefinitions,
  createKpiAlert,
  type KpiAlertWire,
  type KpiDefinitionWire,
} from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

export function AlertConfigurationPage(): JSX.Element {
  const qc = useQueryClient();
  const [kpiId, setKpiId] = useState('');
  const [severity, setSeverity] = useState<'INFO' | 'WARN' | 'CRITICAL'>('WARN');
  const [condition, setCondition] = useState<'ABOVE' | 'BELOW'>('ABOVE');
  const [threshold, setThreshold] = useState('');
  const [recipients, setRecipients] = useState('');
  const [notifyMode, setNotifyMode] = useState<'ONCE' | 'EVERY_RUN' | 'DAILY_DIGEST'>('ONCE');
  const [formError, setFormError] = useState<string | null>(null);

  const alerts = useQuery<readonly KpiAlertWire[], Error>({
    queryKey: ['reporting', 'alerts'],
    queryFn: () => listKpiAlerts(),
    retry: false,
  });

  const kpis = useQuery<readonly KpiDefinitionWire[], Error>({
    queryKey: ['reporting', 'kpi-definitions'],
    queryFn: () => listKpiDefinitions(),
    retry: false,
  });

  const mutation = useMutation({
    mutationFn: () => {
      const recipientList = recipients.split(',').map((s) => s.trim()).filter(Boolean);
      return createKpiAlert({
        kpiDefinitionId: kpiId,
        severity,
        recipients: recipientList,
        condition,
        threshold: Number(threshold),
        notifyMode,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['reporting', 'alerts'] });
      void qc.invalidateQueries({ queryKey: ['reporting', 'dashboard'] });
      setKpiId('');
      setThreshold('');
      setRecipients('');
      setFormError(null);
    },
    onError: (err) => {
      setFormError(err instanceof Error ? err.message : 'Failed to create alert');
    },
  });

  const denied =
    alerts.isError &&
    alerts.error instanceof ApiError &&
    (alerts.error.status === 401 || alerts.error.status === 403);

  function submit(): void {
    const th = Number(threshold);
    const recipientList = recipients.split(',').map((s) => s.trim()).filter(Boolean);
    if (!kpiId) { setFormError('Select a KPI definition'); return; }
    if (!Number.isFinite(th)) { setFormError('Threshold must be a number'); return; }
    if (recipientList.length === 0) { setFormError('At least one recipient email required'); return; }
    mutation.mutate();
  }

  const kpiList = kpis.data ?? [];
  const effectiveKpiId = kpiId || (kpiList[0]?.id ?? '');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Alert configuration"
        description="Trigger notifications when reporting KPIs breach thresholds."
      />

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/manageAlerts</code> required.
        </DeferredNotice>
      )}

      {!denied && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Bell className="w-4 h-4 text-nipost-green" />
            <h3 className="font-semibold text-gray-900">New alert rule</h3>
          </div>
          {formError && (
            <p className="text-xs text-red-600 mb-3">{formError}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">KPI definition</label>
              <select
                value={effectiveKpiId}
                onChange={(e) => setKpiId(e.target.value)}
                aria-label="KPI definition"
                data-testid="alert-kpi-select"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              >
                {kpiList.length === 0 && (
                  <option value="">— create a KPI definition first —</option>
                )}
                {kpiList.map((k) => (
                  <option key={k.id} value={k.id}>{k.name}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Severity</label>
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value as typeof severity)}
                aria-label="Severity"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              >
                <option value="INFO">Info</option>
                <option value="WARN">Warning</option>
                <option value="CRITICAL">Critical</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Condition</label>
              <select
                value={condition}
                onChange={(e) => setCondition(e.target.value as typeof condition)}
                aria-label="Condition"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              >
                <option value="ABOVE">Above threshold</option>
                <option value="BELOW">Below threshold</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Threshold</label>
              <input
                type="number"
                value={threshold}
                onChange={(e) => setThreshold(e.target.value)}
                placeholder="e.g. 1000000"
                aria-label="Threshold"
                data-testid="alert-threshold-input"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Recipients (comma-separated)</label>
              <input
                type="text"
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                placeholder="ops@nipost.gov.ng, analyst@nipost.gov.ng"
                aria-label="Recipients"
                data-testid="alert-recipients-input"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted">Notify mode</label>
              <select
                value={notifyMode}
                onChange={(e) => setNotifyMode(e.target.value as typeof notifyMode)}
                aria-label="Notify mode"
                className="px-3 py-2 text-sm border border-border rounded-lg"
              >
                <option value="ONCE">Once</option>
                <option value="EVERY_RUN">Every run</option>
                <option value="DAILY_DIGEST">Daily digest</option>
              </select>
            </div>
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={mutation.isPending}
            data-testid="alert-save-btn"
            className="mt-4 inline-flex items-center gap-1 px-4 py-2 text-xs font-medium text-white bg-nipost-green rounded-lg hover:bg-nipost-green/90 transition-colors disabled:opacity-50"
          >
            <Plus className="w-3.5 h-3.5" />
            {mutation.isPending ? 'Saving…' : 'Create alert rule'}
          </button>
        </div>
      )}

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h3 className="font-semibold text-gray-900">
            Alert rules ({alerts.data?.length ?? 0})
          </h3>
        </div>
        {alerts.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : alerts.isError && !denied ? (
          <p className="text-sm text-red-600">{alerts.error.message}</p>
        ) : alerts.data && alerts.data.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-3 py-2">KPI</th>
                  <th className="text-left px-3 py-2">Severity</th>
                  <th className="text-left px-3 py-2">Condition</th>
                  <th className="text-right px-3 py-2">Threshold</th>
                  <th className="text-left px-3 py-2">Notify</th>
                  <th className="text-left px-3 py-2">Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {alerts.data.map((a) => (
                  <tr key={a.id} data-testid="alert-row">
                    <td className="px-3 py-2 font-mono text-xs">{a.kpiDefinitionId.slice(0, 8)}…</td>
                    <td className="px-3 py-2 text-xs">{a.severity}</td>
                    <td className="px-3 py-2 text-xs">{a.condition}</td>
                    <td className="px-3 py-2 text-right text-xs">{a.threshold.toLocaleString('en-US')}</td>
                    <td className="px-3 py-2 text-xs">{a.notifyMode}</td>
                    <td className="px-3 py-2 text-xs">{a.active ? 'Yes' : 'No'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-muted">No alert rules configured.</p>
        )}
      </div>
    </div>
  );
}
